"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface BreadcrumbTitles {
  readonly [key: string]: string | null;
}

interface BreadcrumbHrefs {
  readonly [key: string]: string | null;
}

interface PageBadge {
  readonly label: string;
  readonly variant?: "default" | "blue" | "green" | "amber";
}

interface BreadcrumbContextValue {
  readonly pageTitle: string | null;
  readonly setPageTitle: (title: string | null) => void;
  /** Small badge rendered next to the current page name in the breadcrumb */
  readonly pageBadge: PageBadge | null;
  readonly setPageBadge: (badge: PageBadge | null) => void;
  /** Get a custom title for a specific segment (UUID or path segment) */
  readonly getSegmentTitle: (segment: string) => string | null;
  /** Set a custom title for a specific segment */
  readonly setSegmentTitle: (segment: string, title: string | null) => void;
  /** Get a custom href for a specific segment */
  readonly getSegmentHref: (segment: string) => string | null;
  /** Set a custom href for a specific segment (overrides default path) */
  readonly setSegmentHref: (segment: string, href: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(
  undefined,
);

interface BreadcrumbProviderProps {
  readonly children: React.ReactNode;
}

export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [pageBadge, setPageBadge] = useState<PageBadge | null>(null);
  const [segmentTitles, setSegmentTitles] = useState<BreadcrumbTitles>({});
  const [segmentHrefs, setSegmentHrefs] = useState<BreadcrumbHrefs>({});

  const getSegmentTitle = useCallback(
    (segment: string): string | null => {
      return segmentTitles[segment] ?? null;
    },
    [segmentTitles],
  );

  const setSegmentTitle = useCallback(
    (segment: string, title: string | null) => {
      setSegmentTitles((prev) => {
        if (title === null) {
          const { [segment]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [segment]: title };
      });
    },
    [],
  );

  const getSegmentHref = useCallback(
    (segment: string): string | null => {
      return segmentHrefs[segment] ?? null;
    },
    [segmentHrefs],
  );

  const setSegmentHref = useCallback((segment: string, href: string | null) => {
    setSegmentHrefs((prev) => {
      if (href === null) {
        const { [segment]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [segment]: href };
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      pageTitle,
      setPageTitle,
      pageBadge,
      setPageBadge,
      getSegmentTitle,
      setSegmentTitle,
      getSegmentHref,
      setSegmentHref,
    }),
    [
      pageTitle,
      pageBadge,
      getSegmentTitle,
      setSegmentTitle,
      getSegmentHref,
      setSegmentHref,
    ],
  );

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }
  return context;
}
